import type { EmptyEncoding, FieldType } from '@resource-kit/model';

/**
 * Per-`FieldType` value projections used by the form engine (#1797).
 *
 * Two independent jobs live here, and design §5.5 is emphatic that they are
 * NOT the same kind of rule:
 *
 *  - `normalize` / `equal` are FRAMEWORK INVARIANTS. Dirty tracking cannot use
 *    a bare `!==`: `{"a":1,"b":2}` vs `{"b":2,"a":1}`, `1.50` vs `1.5`
 *    and `7` vs `'7'` are all the same value wearing different clothes. Without these, "edit a value and put it
 *    back" ships a no-op PATCH.
 *
 *  - `encodeEmpty` is an INJECTED POLICY, deliberately not a global constant.
 *    This repo's catalog string PATCH is `create.partial()` of
 *    `z.string().optional()` — it accepts `''` and REJECTS `null`
 *    (`backend/src/contexts/kitchen/http/catalog-suppliers.ts::supplierCreate`).
 *    But the
 *    operator note in backend/src/contexts/delivery/http/dispatch-routes.ts is
 *    `z.string().max(2000).nullable()`, where clearing MUST send `null`. So the
 *    value below is only this repo's DEFAULT for the type; any field may
 *    override it via `EntityField.encodeEmpty`, which is how an API adapter
 *    layer speaks for its own endpoint.
 */
export interface FieldValuePolicy {
  /** Canonical comparable form. Never compared with `!==` directly. */
  normalize(value: unknown): unknown;
  /** True when two wire values mean the same thing. */
  equal(a: unknown, b: unknown): boolean;
  /** True when the value means "the operator cleared this field". */
  isEmpty(value: unknown): boolean;
  /** Repo default wire value for a cleared field. Overridable per field. */
  encodeEmpty: EmptyEncoding;
}

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

/** Structural equality over normalized forms (they are primitives or JSON text). */
const strictEqual = (a: unknown, b: unknown): boolean => Object.is(a, b);

// ─── normalizers ───────────────────────────────────────────

const normalizeString = (value: unknown): unknown => (isBlank(value) ? null : String(value));

const normalizeNumber = (value: unknown): unknown => {
  if (isBlank(value)) return null;
  const n = typeof value === 'number' ? value : Number(value);
  // A non-numeric draft (mid-typing garbage) keeps its text so the operator's
  // input still round-trips through dirty tracking instead of collapsing to NaN.
  return Number.isFinite(n) ? n : String(value);
};

/**
 * Canonical decimal string: `1.50`, `01.5` and `1.5` are one value. Money is a
 * decimal STRING on the wire (ADR-0011), so this must not go through a float.
 */
const canonicalDecimal = (raw: string): string => {
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(raw.trim());
  if (!match) return raw.trim();
  const sign = match[1] === '-' ? '-' : '';
  const int = (match[2] ?? '').replace(/^0+(?=\d)/, '') || '0';
  const frac = (match[3] ?? '').replace(/0+$/, '');
  const body = frac.length > 0 ? `${int}.${frac}` : int;
  return body === '0' ? '0' : `${sign}${body}`;
};

const normalizeCurrency = (value: unknown): unknown =>
  isBlank(value) ? null : canonicalDecimal(String(value));

const normalizeDate = (value: unknown): unknown => {
  if (isBlank(value)) return null;
  // A date column holds `YYYY-MM-DD` only (ADR-0011 decision one), and the
  // DatePicker emits exactly that, so the calendar day compares as text. Any
  // other value keeps its text rather than being cut to a date (#2875).
  return String(value);
};

const normalizeDateTime = (value: unknown): unknown => {
  if (isBlank(value)) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isNaN(ms) ? String(value) : ms;
};

const normalizeBoolean = (value: unknown): unknown =>
  value === null || value === undefined || value === '' ? null : Boolean(value);

/** Stable stringify with sorted object keys — key order is not a change. */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
};

const normalizeJson = (value: unknown): unknown => {
  if (isBlank(value)) return null;
  if (typeof value === 'string') {
    try {
      return stableStringify(JSON.parse(value) as unknown);
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      // Unparseable draft text compares as itself; the zod layer reports it.
      return value;
    }
  }
  return stableStringify(value);
};

function policy(
  normalize: (value: unknown) => unknown,
  encodeEmpty: EmptyEncoding,
): FieldValuePolicy {
  return {
    normalize,
    equal: (a, b) => strictEqual(normalize(a), normalize(b)),
    isEmpty: isBlank,
    encodeEmpty,
  };
}

/**
 * Repo defaults. String-shaped columns clear to `''` because the catalog PATCH
 * rejects `null`; everything else clears to `null`, which is what a non-string
 * column can actually represent. Where an endpoint disagrees, the field says so
 * — see `EntityField.encodeEmpty`.
 */
export const FIELD_VALUE_POLICY = {
  text: policy(normalizeString, ''),
  email: policy(normalizeString, ''),
  phone: policy(normalizeString, ''),
  id: policy(normalizeString, ''),
  image: policy(normalizeString, ''),
  enum: policy(normalizeString, ''),
  number: policy(normalizeNumber, null),
  currency: policy(normalizeCurrency, null),
  date: policy(normalizeDate, null),
  datetime: policy(normalizeDateTime, null),
  boolean: policy(normalizeBoolean, null),
  json: policy(normalizeJson, null),
  array: policy(normalizeJson, null),
} as const satisfies Record<FieldType, FieldValuePolicy>;

export function fieldValuePolicy(type: FieldType): FieldValuePolicy {
  return FIELD_VALUE_POLICY[type];
}
